import { useBreakpoint } from "./use-breakpoint";

type HapticStyle = "light" | "medium" | "heavy" | "success" | "warning" | "error";

/**
 * Triggers a subtle haptic feedback (vibration) on supported devices
 * Moved to outer scope as it doesn't depend on hook state
 */
const triggerHapticFeedback = (style: HapticStyle = "light") => {
  // Check if vibration API is available (it's only on mobile browsers)
  if (!("vibrate" in navigator)) return;

  switch (style) {
    case "light": {
      navigator.vibrate(10);
      break;
    }
    case "medium": {
      navigator.vibrate(20);
      break;
    }
    case "heavy": {
      navigator.vibrate(40);
      break;
    }
    case "success": {
      navigator.vibrate([10, 30, 10]);
      break;
    }
    case "warning": {
      navigator.vibrate([20, 60, 20]);
      break;
    }
    case "error": {
      navigator.vibrate([40, 40, 40, 40]);
      break;
    }
  }
};

/**
 * Hook that provides touch optimization utilities
 * Returns touch-optimized props based on current breakpoint
 */
export const useTouchOptimization = () => {
  const { isMobile } = useBreakpoint();

  return {
    isMobile,
    triggerHaptic: triggerHapticFeedback,
    // Touch-optimized button props
    buttonProps: {
      touchOptimized: isMobile,
      size: isMobile ? ("touch" as const) : ("default" as const),
    },
    // Touch-optimized input props
    inputProps: {
      touchOptimized: isMobile,
    },
    // Touch-optimized toggle props
    toggleProps: {
      size: isMobile ? ("touch" as const) : ("md" as const),
    },
    // Helper to get touch-optimized classes
    getTouchClasses: (baseClasses = "") => {
      const touchClasses = isMobile ? "touch-manipulation" : "";
      return `${baseClasses} ${touchClasses}`.trim();
    },
    // Helper to get minimum touch target size classes
    getTouchTargetClasses: () => {
      return isMobile ? "min-h-12 min-w-12" : ""; // Increased to 48px (12 * 4) standard
    },
  };
};

/**
 * Utility function to wrap components with touch optimization
 */
export const withTouchOptimization = <T extends Record<string, unknown>>(
  props: T,
  isMobile: boolean,
): T => {
  if (!isMobile) return props;

  // Apply touch optimization to known props
  const optimizedProps = { ...props };

  // Auto-enhance button/input sizes
  if ("size" in props && !("touchOptimized" in props && props.touchOptimized)) {
    const sizeMap: Record<string, string> = {
      sm: "touch-sm",
      md: "touch",
      lg: "touch-lg",
      icon: "icon-lg",
      default: "touch",
    };
    const currentSize = String(props.size);
    (optimizedProps as Record<string, unknown>).size = sizeMap[currentSize] ?? currentSize;
  }

  // Add touch manipulation
  if ("className" in props && typeof props.className === "string") {
    (optimizedProps as Record<string, unknown>).className =
      `${props.className} touch-manipulation`.trim();
  }

  return optimizedProps;
};
